import { ensureFreshToken, isKeycloakInitialized } from './auth/keycloak';
import { ApiRequestError, type ApiError } from './apiError';

const API_HOST_BY_DASHBOARD_HOST: Record<string, string> = {
  'localhost:5173': 'http://localhost:8080',
  'dashboard-stg.lutiq.com': 'https://lutiq-website-802468048606.us-west2.run.app',
  'dashboard.lutiq.com': 'https://api.lutiq.com',
};

export const getApiBaseUrl = (): string => {
  const envOverride = import.meta.env.VITE_API_BASE_URL;
  if (envOverride) {
    return envOverride;
  }

  if (typeof window === 'undefined') {
    return '';
  }

  const host = window.location.host;
  const hostname = window.location.hostname;

  return API_HOST_BY_DASHBOARD_HOST[host] ?? API_HOST_BY_DASHBOARD_HOST[hostname] ?? '';
};

export const buildApiUrl = (path: string): string => {
  const baseUrl = getApiBaseUrl();
  return `${baseUrl}${path}`;
};

// `ApiError` + `ApiRequestError` now live in ./apiError (a zero-dependency leaf
// module so auth/authErrors.ts can import the class without forming an
// `authErrors → api → keycloak → authErrors` import cycle). Re-exported here so
// the ~40 existing `import { ApiRequestError, type ApiError } from '.../api'`
// call sites keep resolving through this barrel unchanged.
export { ApiRequestError, type ApiError };

/**
 * True when `headers` already carries `name` (case-insensitive), across every
 * shape `RequestInit.headers` can take: `Headers`, `[key, value][]`, or a plain
 * object. Used so we never clobber a Content-Type the caller set deliberately.
 */
const headersHave = (headers: HeadersInit | undefined, name: string): boolean => {
  if (!headers) return false;
  const target = name.toLowerCase();
  if (headers instanceof Headers) {
    return headers.has(name);
  }
  if (Array.isArray(headers)) {
    return headers.some(([key]) => key.toLowerCase() === target);
  }
  return Object.keys(headers).some((key) => key.toLowerCase() === target);
};

/**
 * Returns a copy of `init` with `[name]: value` merged into its headers,
 * preserving whichever of the three header shapes the caller used. Does not
 * mutate the caller's object (except a `Headers` instance, which is set in
 * place — `resolvedInit` already holds a shallow copy of `init`).
 */
const withHeader = (init: RequestInit, name: string, value: string): RequestInit => {
  const { headers } = init;
  if (!headers) {
    return { ...init, headers: { [name]: value } };
  }
  if (headers instanceof Headers) {
    headers.set(name, value);
    return init;
  }
  if (Array.isArray(headers)) {
    return { ...init, headers: [...headers, [name, value]] };
  }
  return { ...init, headers: { ...headers, [name]: value } };
};

export const apiFetch = async (
  pathOrUrl: string,
  init?: RequestInit,
  authRequired = true
): Promise<Response> => {
  const isAbsolute = /^https?:\/\//.test(pathOrUrl);
  const url = isAbsolute ? pathOrUrl : buildApiUrl(pathOrUrl);
  let resolvedInit: RequestInit | undefined = init ? { ...init } : undefined;

  // Default Content-Type to application/json when the caller sends a string
  // body (i.e. JSON.stringify(...)) without setting one. The shared helper
  // previously omitted Content-Type entirely, so the browser defaulted to
  // `text/plain` and the Quarkus backend's `@Consumes(application/json)`
  // endpoints rejected with HTTP 415. We only touch string bodies: FormData,
  // Blob, and URLSearchParams rely on the browser setting the correct
  // Content-Type (and multipart boundary), and an explicit caller-set
  // Content-Type always wins.
  if (
    resolvedInit
    && typeof resolvedInit.body === 'string'
    && !headersHave(resolvedInit.headers, 'Content-Type')
  ) {
    resolvedInit = withHeader(resolvedInit, 'Content-Type', 'application/json');
  }

  if (authRequired && isKeycloakInitialized()) {
    const token = await ensureFreshToken();
    if (token) {
      if (!resolvedInit) {
        return fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      resolvedInit = withHeader(resolvedInit, 'Authorization', `Bearer ${token}`);
    }
  }

  return resolvedInit ? fetch(url, resolvedInit) : fetch(url, undefined);
};

export const requestJson = async <T>(
  pathOrUrl: string,
  init?: RequestInit,
  authRequired = true
): Promise<T> => {
  const response = await apiFetch(pathOrUrl, init, authRequired);
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const apiError = payload as ApiError | null;
    throw new ApiRequestError(
      apiError?.message ?? `Request failed (${response.status}).`,
      response.status,
      apiError?.details,
      payload,
    );
  }
  return payload as T;
};

export const apiRequestJson = requestJson;
export const publicRequestJson = <T>(pathOrUrl: string, init?: RequestInit) =>
  requestJson<T>(pathOrUrl, init, false);
